import { useState, useMemo, useCallback, useEffect } from 'react';
import { usePersistedFilterState } from '@/hooks/usePersistedFilterState';
import { FilterRule, FilterField, FilterGroup, FilterEntity, FilterAttribute } from '@/types/filters';
import { supabase } from '@/integrations/supabase/client';

type Option = { value: string; label: string };

// Define the LineItem interface based on the existing component
interface LineItem {
  id: string;
  title?: string;
  description?: string;
  base_unit: string;
  base_cost: number;
  conversion_unit?: string;
  conversion_factor?: number;
  productivity_unit?: string;
  productivity_rate?: number;
  tags?: string[];
  is_active: boolean;
  image_urls?: string[];
  category?: string;
  trade?: string;
  created_at: string;
  updated_at: string;
  internal_pricing_tiers: any[];
  markup_percentage?: number;
  markup_source?: string;
  final_price?: number;
  company_id: string;
  created_by: string;
}

export const useInternalLineItemFilterBuilder = (lineItems: LineItem[]) => {
  const [draftGroups, setDraftGroups] = usePersistedFilterState<FilterGroup[]>('filters-internal-line-items-draft', []);
  const [appliedGroups, setAppliedGroups] = usePersistedFilterState<FilterGroup[]>('filters-internal-line-items-applied', []);

  const [optionsOverride, setOptionsOverride] = useState<{
    categories: Option[];
    trades: Option[];
    tags: Option[];
    baseUnits: Option[];
    createdBy: Option[];
    markupSources: Option[];
  }>({ 
    categories: [], 
    trades: [], 
    tags: [],
    baseUnits: [],
    createdBy: [],
    markupSources: []
  });

  // Fetch comprehensive filter options from database
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch profiles for created_by options and internal line items for categories/trades
        const [profilesRes, lineItemsRes] = await Promise.all([
          supabase.from('profiles').select('id, first_name, last_name'),
          supabase.from('internal_line_items').select('category, trade, tags, base_unit, markup_source')
        ]);

        if (profilesRes.error) throw profilesRes.error;
        if (lineItemsRes.error) throw lineItemsRes.error;

        const categoriesSet = new Set<string>();
        const tradesSet = new Set<string>();
        const tagsSet = new Set<string>();
        const baseUnitsSet = new Set<string>();
        const markupSourcesSet = new Set<string>();

        (lineItemsRes.data || []).forEach((item: any) => {
          if (item.category) categoriesSet.add(item.category);
          if (item.trade) tradesSet.add(item.trade);
          if (item.base_unit) baseUnitsSet.add(item.base_unit);
          if (item.markup_source) markupSourcesSet.add(item.markup_source);
          
          if (Array.isArray(item.tags)) {
            item.tags.filter(Boolean).forEach((t: string) => tagsSet.add(t));
          }
        });

        const createdByOptions: Option[] = (profilesRes.data || []).map((profile: any) => ({
          value: profile.id,
          label: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown'
        }));

        setOptionsOverride({
          categories: Array.from(categoriesSet).map(cat => ({ value: cat, label: cat })),
          trades: Array.from(tradesSet).map(trade => ({ value: trade, label: trade })),
          tags: Array.from(tagsSet).map(tag => ({ value: tag, label: tag })),
          baseUnits: Array.from(baseUnitsSet).map(unit => ({ value: unit, label: unit })),
          createdBy: createdByOptions,
          markupSources: Array.from(markupSourcesSet).map(source => ({ value: source, label: source }))
        });
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };

    fetchOptions();
  }, []);

  // Generate dynamic options from the current line items data
  const dynamicOptions = useMemo(() => {
    const categories = [...new Set(
      lineItems
        .map(item => item.category)
        .filter(Boolean)
    )].map(cat => ({ value: cat!, label: cat! }));

    const trades = [...new Set(
      lineItems
        .map(item => item.trade)
        .filter(Boolean)
    )].map(trade => ({ value: trade!, label: trade! }));

    const baseUnits = [...new Set(
      lineItems
        .map(item => item.base_unit)
        .filter(Boolean)
    )].map(unit => ({ value: unit!, label: unit! }));

    const createdBy = [...new Set(
      lineItems
        .map(item => item.created_by)
        .filter(Boolean)
    )].map(id => ({ value: id!, label: id! }));

    return {
      categories: optionsOverride.categories.length ? optionsOverride.categories : categories,
      trades: optionsOverride.trades.length ? optionsOverride.trades : trades,
      baseUnits: optionsOverride.baseUnits.length ? optionsOverride.baseUnits : baseUnits,
      createdBy: optionsOverride.createdBy.length ? optionsOverride.createdBy : createdBy,
      tags: optionsOverride.tags,
      markupSources: optionsOverride.markupSources,
    };
  }, [lineItems, optionsOverride]);

  // Filter configuration - 3 main entities
  const filterEntities: FilterEntity[] = [
    { key: 'line_item', label: 'Line Item' },
    { key: 'categorization', label: 'Categorization' },
    { key: 'pricing', label: 'Pricing' }
  ];

  // Attributes per entity
  const filterAttributesByEntity: Record<string, FilterAttribute[]> = {
    line_item: [
      { key: 'title', label: "'s Title", type: 'text' },
      { key: 'description', label: "'s Description", type: 'text' },
      { key: 'base_unit', label: "'s Base Unit", type: 'select', options: dynamicOptions.baseUnits },
      { key: 'base_cost', label: "'s Base Cost", type: 'number' },
      { key: 'final_price', label: "'s Final Price", type: 'number' },
      { key: 'is_active', label: 'Is Active', type: 'boolean' },
      { key: 'created_at', label: "'s Date Created", type: 'date' },
      { key: 'updated_at', label: "'s Date Updated", type: 'date' },
      { key: 'has_conversion', label: 'Has Conversion Factor', type: 'boolean' },
      { key: 'has_productivity', label: 'Has Productivity Rate', type: 'boolean' },
      { key: 'has_images', label: 'Has Images', type: 'boolean' },
      { key: 'created_by', label: "'s Created By", type: 'select', options: dynamicOptions.createdBy }
    ],
    categorization: [
      { key: 'category', label: "'s Category", type: 'select', options: dynamicOptions.categories },
      { key: 'trade', label: "'s Trade", type: 'select', options: dynamicOptions.trades },
      { key: 'tags', label: "'s Tags", type: 'select', options: dynamicOptions.tags },
      { key: 'has_category', label: 'Has Category', type: 'boolean' },
      { key: 'has_trade', label: 'Has Trade', type: 'boolean' },
      { key: 'has_tags', label: 'Has Tags', type: 'boolean' }
    ],
    pricing: [
      { key: 'markup_percentage', label: "'s Markup Percentage", type: 'number' },
      { key: 'markup_source', label: "'s Markup Source", type: 'select', options: dynamicOptions.markupSources },
      { key: 'has_pricing_tiers', label: 'Has Pricing Tiers', type: 'boolean' },
      { key: 'pricing_tier_count', label: "'s Number of Pricing Tiers", type: 'number' },
      { key: 'has_custom_markup', label: 'Has Custom Markup', type: 'boolean' }
    ]
  };

  // Generate filter fields for the FilterBuilder component
  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [];
    
    Object.entries(filterAttributesByEntity).forEach(([entityKey, attributes]) => {
      attributes.forEach(attr => {
        const entityLabel = filterEntities.find(e => e.key === entityKey)?.label || entityKey;
        fields.push({
          key: `${entityKey}.${attr.key}`,
          label: `${entityLabel}${attr.label}`,
          type: attr.type,
          options: attr.options
        });
      });
    });

    return fields;
  }, [dynamicOptions]);

  const addGroup = useCallback(() => {
    const newGroup: FilterGroup = {
      id: `group_${Date.now()}_${Math.random()}`,
      rules: [{
        id: `rule_${Date.now()}_${Math.random()}`,
        entity: '',
        attribute: '',
        operator: 'equals',
        value: null,
        logic: undefined,
      }],
      logic: draftGroups.length > 0 ? 'AND' : 'AND',
    };
    setDraftGroups(prev => [...prev, newGroup]);
  }, [draftGroups.length]);

  const updateGroup = useCallback((groupId: string, updates: Partial<FilterGroup>) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId ? { ...group, ...updates } : group
    ));
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    setDraftGroups(prev => prev.filter(group => group.id !== groupId));
  }, []);

  const addRuleToGroup = useCallback((groupId: string) => {
    const newRule: FilterRule = {
      id: `rule_${Date.now()}_${Math.random()}`,
      entity: '',
      attribute: '',
      operator: 'equals',
      value: null,
      logic: 'AND',
    };
    
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, rules: [...group.rules, newRule] }
        : group
    ));
  }, []);

  const updateRuleInGroup = useCallback((groupId: string, ruleId: string, updates: Partial<FilterRule>) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? {
            ...group,
            rules: group.rules.map(rule => 
              rule.id === ruleId ? { ...rule, ...updates } : rule
            )
          }
        : group
    ));
  }, []);

  const removeRuleFromGroup = useCallback((groupId: string, ruleId: string) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId 
        ? { ...group, rules: group.rules.filter(rule => rule.id !== ruleId) }
        : group
    ));
  }, []);

  const clearAllRules = useCallback(() => {
    setDraftGroups([]);
    setAppliedGroups([]);
  }, []);

  // Helper function to check if a rule is complete
  const isRuleComplete = useCallback((rule: FilterRule): boolean => {
    if (!rule.entity || !rule.attribute || !rule.operator) return false;
    
    // For operators that don't need values
    if (['is_empty', 'is_not_empty'].includes(rule.operator)) return true;
    
    // For other operators, check if value exists
    return rule.value !== null && rule.value !== undefined && rule.value !== '';
  }, []);

  const applyFilters = useCallback(() => {
    // Only apply complete rules
    const completeGroups = draftGroups
      .map(group => ({
        ...group,
        rules: group.rules.filter(isRuleComplete)
      }))
      .filter(group => group.rules.length > 0);
    
    setAppliedGroups(completeGroups);
  }, [draftGroups, isRuleComplete]);

  // Helper function to get field value from line item
  const getFieldValue = useCallback((item: LineItem, rule: FilterRule): any => {
    const { entity, attribute } = rule;
    
    switch (entity) {
      case 'line_item':
        switch (attribute) {
          case 'title': return item.title || '';
          case 'description': return item.description || '';
          case 'base_unit': return item.base_unit;
          case 'base_cost': return item.base_cost;
          case 'final_price': return item.final_price || 0;
          case 'is_active': return item.is_active;
          case 'created_at': return new Date(item.created_at);
          case 'updated_at': return new Date(item.updated_at);
          case 'has_conversion': return !!(item.conversion_factor && item.conversion_unit);
          case 'has_productivity': return !!(item.productivity_rate && item.productivity_unit);
          case 'has_images': return !!(item.image_urls && item.image_urls.length > 0);
          case 'created_by': return item.created_by;
          default: return null;
        }
      case 'categorization':
        switch (attribute) {
          case 'category': return item.category || '';
          case 'trade': return item.trade || '';
          case 'tags': return item.tags || [];
          case 'has_category': return !!item.category;
          case 'has_trade': return !!item.trade;
          case 'has_tags': return !!(item.tags && item.tags.length > 0);
          default: return null;
        }
      case 'pricing':
        switch (attribute) {
          case 'markup_percentage': return item.markup_percentage || 0;
          case 'markup_source': return item.markup_source || '';
          case 'has_pricing_tiers': return !!(item.internal_pricing_tiers && item.internal_pricing_tiers.length > 0);
          case 'pricing_tier_count': return item.internal_pricing_tiers ? item.internal_pricing_tiers.length : 0;
          case 'has_custom_markup': return !!(item.markup_percentage && item.markup_percentage > 0);
          default: return null;
        }
      default:
        return null;
    }
  }, []);

  // Rule evaluation logic
  const evaluateRule = useCallback((item: LineItem, rule: FilterRule): boolean => {
    if (!isRuleComplete(rule)) return true; // Skip incomplete rules

    const fieldValue = getFieldValue(item, rule);
    const { operator, value } = rule;

    switch (operator) {
      case 'equals':
        if (typeof fieldValue === 'boolean') {
          return fieldValue === Boolean(value);
        }
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return fieldValue.some(v => value.map(x => String(x).toLowerCase()).includes(String(v).toLowerCase()));
          }
          return value.map(x => String(x).toLowerCase()).includes(String(fieldValue).toLowerCase());
        }
        if (Array.isArray(fieldValue)) {
          return fieldValue.some(v => String(v).toLowerCase() === String(value).toLowerCase());
        }
        return String(fieldValue).toLowerCase() === String(value).toLowerCase();
      
      case 'not_equals':
        if (typeof fieldValue === 'boolean') {
          return fieldValue !== Boolean(value);
        }
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return !fieldValue.some(v => value.map(x => String(x).toLowerCase()).includes(String(v).toLowerCase()));
          }
          return !value.map(x => String(x).toLowerCase()).includes(String(fieldValue).toLowerCase());
        }
        if (Array.isArray(fieldValue)) {
          return !fieldValue.some(v => String(v).toLowerCase() === String(value).toLowerCase());
        }
        return String(fieldValue).toLowerCase() !== String(value).toLowerCase();
      
      case 'contains':
        if (Array.isArray(fieldValue)) {
          return fieldValue.some(v => String(v).toLowerCase().includes(String(value).toLowerCase()));
        }
        return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
      
      case 'not_contains':
        if (Array.isArray(fieldValue)) {
          return !fieldValue.some(v => String(v).toLowerCase().includes(String(value).toLowerCase()));
        }
        return !String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
      
      case 'greater_than':
        return Number(fieldValue) > Number(value);
      
      case 'less_than':
        return Number(fieldValue) < Number(value);
      
      case 'between':
        if (typeof value === 'object' && value?.min !== undefined && value?.max !== undefined) {
          const numValue = Number(fieldValue);
          return numValue >= Number(value.min) && numValue <= Number(value.max);
        }
        return false;
      
      case 'in':
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return fieldValue.some(fv => value.some(v => String(fv).toLowerCase() === String(v).toLowerCase()));
          }
          return value.some(v => String(fieldValue).toLowerCase() === String(v).toLowerCase());
        }
        return false;
      
      case 'not_in':
        if (Array.isArray(value)) {
          if (Array.isArray(fieldValue)) {
            return !fieldValue.some(fv => value.some(v => String(fv).toLowerCase() === String(v).toLowerCase()));
          }
          return !value.some(v => String(fieldValue).toLowerCase() === String(v).toLowerCase());
        }
        return true;
      
      case 'is_empty':
        if (Array.isArray(fieldValue)) return fieldValue.length === 0;
        if (typeof fieldValue === 'boolean') return false; // boolean fields are never empty
        return !fieldValue || String(fieldValue).trim() === '';
      
      case 'is_not_empty':
        if (Array.isArray(fieldValue)) return fieldValue.length > 0;
        if (typeof fieldValue === 'boolean') return true; // boolean fields are never empty
        return !!fieldValue && String(fieldValue).trim() !== '';
      
      default:
        return true;
    }
  }, [getFieldValue, isRuleComplete]);

  // Filter the line items based on applied groups
  const filteredLineItems = useMemo(() => {
    if (appliedGroups.length === 0) return lineItems;

    return lineItems.filter(item => {
      // All groups must match (AND between groups)
      return appliedGroups.every(group => {
        if (group.rules.length === 0) return true;

        // Apply group logic (AND/OR between rules within group)
        if (group.logic === 'OR') {
          return group.rules.some(rule => evaluateRule(item, rule));
        } else {
          return group.rules.every(rule => evaluateRule(item, rule));
        }
      });
    });
  }, [lineItems, appliedGroups, evaluateRule]);

  // Computed state
  const hasActiveFilters = appliedGroups.length > 0 && appliedGroups.some(group => group.rules.length > 0);
  const hasUnappliedChanges = JSON.stringify(draftGroups) !== JSON.stringify(appliedGroups);
  const canApplyFilters = draftGroups.length > 0 && draftGroups.some(group => 
    group.rules.some(rule => isRuleComplete(rule))
  );

  return {
    // State
    draftGroups,
    appliedGroups,
    filteredLineItems,
    
    // Actions
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    clearAllRules,
    applyFilters,
    
    // Configuration
    filterFields,
    filterEntities,
    filterAttributesByEntity,
    
    // Computed state
    hasActiveFilters,
    hasUnappliedChanges,
    canApplyFilters,
  };
};